import { CheckboxField, DatePickerField, Flex, Form } from '@/components';
import { OrganizationSearchFormSchema } from '@/features/tenant-manager/organization/_models/organization';
import { useForm } from '@/hooks';
import { zodResolver } from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import { type FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import type { z } from 'zod';

type FormValues = z.infer<typeof OrganizationSearchFormSchema>;

export const OrganizationSearchForm: FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentParams = Object.fromEntries(searchParams.entries());
  const {
    control,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      ...currentParams,
      referenceFiscalYear: currentParams.referenceFiscalYear
        ? dayjs().year(Number(currentParams.referenceFiscalYear))
        : undefined,
      showExcludedOptions: currentParams.showExcludedOptions === 'true',
    },
    resolver: zodResolver(OrganizationSearchFormSchema),
  });

  const [referenceFiscalYear, showExcludedOptions] = watch([
    'referenceFiscalYear',
    'showExcludedOptions',
  ]);

  useEffect(() => {
    // TODO: debounce to prevent too many history push
    referenceFiscalYear
      ? searchParams.set(
          'referenceFiscalYear',
          referenceFiscalYear.year().toString(),
        )
      : searchParams.delete('referenceFiscalYear');
    showExcludedOptions
      ? searchParams.set('showExcludedOptions', 'true')
      : searchParams.delete('showExcludedOptions');

    setSearchParams(searchParams, { replace: true });
  }, [referenceFiscalYear, showExcludedOptions, searchParams, setSearchParams]);

  return (
    <Form leavePageAlertCondition={null} layout="vertical">
      <Flex gap={16} align="flex-end">
        <DatePickerField
          name="referenceFiscalYear"
          label={t('reference-fiscal-year')}
          picker="year"
          control={control}
          errors={errors}
          fieldStyle={{ width: '100%' }}
        />
        <CheckboxField
          text={t('show-excluded-options')}
          name="showExcludedOptions"
          control={control}
          errors={errors}
        />
      </Flex>
    </Form>
  );
};
